import { Injectable } from '@angular/core';
import { DocumentTableColumn } from '../_models/document';
import { TableButton } from '../_models/table-buttons';
import { BehaviorSubject, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DocumentsService } from './documents.service';


@Injectable({
  providedIn: 'root'
})
export class DynamicTableService {

  private tableColumns: BehaviorSubject<DocumentTableColumn[]>;
  public tableColumns$: Observable<DocumentTableColumn[]>;
  private buttonsConfig: BehaviorSubject<TableButton[]>;
  public buttonsConfig$: Observable<TableButton[]>;

  
  constructor(
    private translateService: TranslateService,
    private documentsService: DocumentsService) {
    this.tableColumns = new BehaviorSubject<DocumentTableColumn[]>(JSON.parse(localStorage.getItem('tableColumns')));
    this.tableColumns$ = this.tableColumns.asObservable();
    this.buttonsConfig = new BehaviorSubject<TableButton[]>(JSON.parse(localStorage.getItem('buttonsConfig')));
    this.buttonsConfig$ = this.buttonsConfig.asObservable();
  }

  // Simple sorting algorithm that supports characters, numbers, booleans and ISO date
  public sortColumns(direction, renderedData, columnData) {
    let sortBy = columnData.searchType;
    try {
      renderedData.sort((a, b) => {
        let condition;
        switch (sortBy) {
          case 'date':
            condition = direction === 'asc' ? new Date(a[columnData['name']]).getTime() - new Date(b[columnData['name']]).getTime() :
              new Date(b[columnData['name']]).getTime() - new Date(a[columnData['name']]).getTime();
            break;
          case 'text':
            condition = direction === 'asc' ? a[columnData['name']].localeCompare(b[columnData['name']]) :
              b[columnData['name']].localeCompare(a[columnData['name']]);
            break;
          case 'boolean':
            condition = direction === 'asc' ? a[columnData['name']] - b[columnData['name']] :
              b[columnData['name']] - a[columnData['name']];
            break;
        }
        return condition;
      });
    }
    catch (err) {
      console.log(`Unable to sort ${columnData.name} column -->`, err);
    }
  }

  // Reset documents table data saving selected ones
  public resetHeaders(documents, originalDocuments) {
    let selectedIds = [];
    // Find documents that have been selected
    documents.forEach(doc => { if (doc.checked) selectedIds.push(doc.id); });
    // Reset all documents
    documents = JSON.parse(JSON.stringify(originalDocuments));
    // Restore selected ones
    documents.forEach(doc => { if (selectedIds.indexOf(doc.id) > -1) doc.checked = true; });
    return documents;
  }

  // Search by text
  public searchByText(text, documents, textFilters, originalDocuments) {
    // Split into pages
    // let localDocuments = this.documentsService.splitIntoSubArray(JSON.parse(JSON.stringify(originalDocuments)));
    
    // Reset documents before filtering so the search is applied to all of them
    documents = this.updateTableWithTextFilters(textFilters, JSON.parse(JSON.stringify(originalDocuments)));
    if (text == null || text === '') {      
      return documents;
    }
    // Filter logic
    documents = documents.filter(doc => { return this.containsText(doc, text) });
    return documents;
  }

  public updateTableWithTextFilters(textFilters, originalDocuments) {
    // let filteredDocs = this.documentsService.splitIntoSubArray(JSON.parse(JSON.stringify(originalDocuments || [])));
    let filteredDocs = JSON.parse(JSON.stringify(originalDocuments || []));
    if (!textFilters || (textFilters && textFilters.length == 0)) {      
      return filteredDocs;
    }    
    textFilters.forEach(filter => {
      if (filteredDocs && filteredDocs.length) {
        filteredDocs = filteredDocs.filter(doc => { return this.containsText(doc, filter) });
      }
    });
    return filteredDocs;
  }

  // Check if an especific document contains an especific piece of text
  private containsText(document, text) {
    let found = false;
    let i = 0;
    let allowedColumns = this.tableColumns.value.map(column => { return column.name });
    let objectKeys = Object.keys(document).filter(o => allowedColumns.indexOf(o) > -1);
    // Loop through current document object
    while (i < objectKeys.length && !found) {
      let key = objectKeys[i];
      let currentRow = document[key] ? document[key].toString() : document[key]; // This is necessary so we do not convert a boolean into a string to compare it in the line bellow
      if (key === 'Signed') {
        currentRow = currentRow ? this.translateService.instant('table.documentsValues.Signed') : this.translateService.instant('table.documentsValues.NotSigned'); // Assigning strings so we can search by text
      }
      found = currentRow.toLowerCase().includes(text.toLowerCase());
      i++;
    }
    return found;
  }

  // Reset column sort
  public resetColumnSort(column) {
    const columns = this.tableColumns.value.map(c => { return c.id == column.id ? {...c, sort: column.sort} : {...c, sort: ''} });
    return columns;
  }

  // Change direction based on previous value
  public changeDirection(sort) {
    let direction = sort;
    switch (sort) {
      case 'asc':
        direction = 'desc';
        break;
      case 'desc':
        direction = '';
        break;
      case '':
        direction = 'asc';
        break;
    }
    return direction;
  }

  public loadTableConfig(config) {
    const { button, columns } = config;
    this.loadButtons(button);
    this.loadColumns(columns);
  }

  private loadButtons(buttons) {
    const buttonsIconsConfig = {
      sign: 'edit',
      download: 'get_app',
      print: 'print',
      email: 'email',
      reject: 'not_interested',
      delete: 'delete'
    };
    let buttonsConfig: TableButton[] = [];
    buttons.forEach(btn => {
      let button: TableButton = btn;
      button.action = button.name.toLowerCase();
      button.icon = buttonsIconsConfig[button.action];
      buttonsConfig.push(button);
    });
    this.setButtonsConfig(buttonsConfig);
  }

  private setButtonsConfig(buttons) {
    localStorage.setItem('buttonsConfig', JSON.stringify(buttons));
    this.buttonsConfig.next(buttons);
  }

  private loadColumns(columns) {
    const columnsConfig: DocumentTableColumn[] = [];
    columns.forEach(col => {
      let newColumn: DocumentTableColumn = col;
      newColumn.sort = '';
      columnsConfig.push(newColumn);
    });
    columnsConfig.unshift({ id: 0, name: "Select", sortable: true, maxLength: 50, searchType: "check", sort: "" })
    this.setColumnsConfig(columnsConfig);
  }

  private setColumnsConfig(columnsConfig) {
    localStorage.setItem('tableColumns', JSON.stringify(columnsConfig));
    this.tableColumns.next(columnsConfig);
  }

  clearStorage() {
    localStorage.removeItem('tableColumns');
    localStorage.removeItem('buttonsConfig');
    this.tableColumns.next(null);
    this.buttonsConfig.next(null);
  }

}
